import React, { Fragment, useRef, useState, useEffect } from "react";
import { getSessionData } from "../utils/auth";
import NavHeader from "../components/navigation/NavHeader";
import Footer from "../components/navigation/Footer";
import GoUpDown from "../components/navigation/GoUpDown";
import AsideLeft from "../components/aside/AsideLeft";
import AsideRight from "../components/aside/AsideRight";
import { Editor } from '@tinymce/tinymce-react';
import { useParams } from "react-router-dom";
import { getPost, getForum, updatePost, deletePost, postLinkPreview } from "../core/communities";
import { useNavigate } from "react-router-dom";
import Loader from 'react-loaders';
import toast, { Toaster } from 'react-hot-toast';
import DOMPurify from "dompurify";
import { Dialog, Transition } from '@headlessui/react';
import ForumHeaderSection from "../components/forum/ForumHeaderSection";
import CommunityLayout from "../components/layouts/CommunityLayout";
import NavUserAvatar from "../components/navigation/NavUserAvatar";
import LinkPreviewUI from '../components/post/LinkPreview';
import PhotoIcon from "../components/icons/Photo";
import RichTextEditor, { getWordCountFromHtml } from '../components/RichTextEditor';
import { usePageTitle } from '../hooks/usePageTitle';



const EditPostForm = () => {
    usePageTitle('Edit Post - Wefayo');
    const { comid, id, pid } = useParams();
    const navigate = useNavigate();

    const TINY_MCE_API_KEY = process.env.REACT_APP_TINY_MCE;
    const editorRef = useRef(null);

    let loggedInUser = getSessionData();

    const [editorKey, setEditorKey] = useState(4);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    const [post, setPost] = useState(null);
    const [title, setTitle] = useState("");
    const [subTitle, setSubTitle] = useState("");
    const [details, setDetails] = useState("<p></p>");
    const [breadcrumbPages, setBreadcrumbPages] = useState([]);
    const [forum, setForum] = useState(null);
    const [postType, setPostType] = useState('post');
    const [linkPreviewed, setLinkPreviewed] = useState();
    const [lastPreviewedUrl, setLastPreviewedUrl] = useState("");

    const CLOUDINARY_URL = process.env.REACT_APP_CLOUDINARY_URL || "";
    const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "";
    const [isFileUpLoading, setIsFileUpLoading] = useState(false);
    const [media, setMedia] = useState("");

    useEffect(() => {
        const fetchPost = async () => {
            const postRes = await getPost(pid);
            setPost(postRes);
            setTitle(postRes.title);
            setSubTitle(postRes.sub_title);
            setMedia(postRes?.media ?? "");

            const details = postRes.description;
            const decodedDetails = atob(details);
            let html = DOMPurify.sanitize(decodedDetails, {
                USE_PROFILES: { html: true },
            });
            html = decodeURIComponent(html);

            setDetails(html);

            setBreadcrumbPages([
                { name: postRes?.forum?.community?.title, href: `/`, current: false },
                { name: postRes?.forum?.title, href: `/community/${postRes?.forum?.community._id}/forum/${postRes?.forum._id}`, current: false },
                ... ((postRes?.title ?? "".length > 0) ? [
                    { name: postRes?.title, href: `/community/${postRes?.forum?.community._id}/forum/${postRes?.forum._id}/post/${postRes?._id}`, current: false },
                    { name: "Edit Post", href: ``, current: true }
                ] : [{ name: "Edit Post", href: ``, current: true }]),
            ]);
            setForum(postRes?.forum);
        }
        fetchPost();
    }, []);

    const clearForm = () => {
        setTitle(post.title);
        setSubTitle(post.sub_title);
        setMedia(post?.media ?? "");

        const details = post.description;
        const decodedDetails = atob(details);
        let html = DOMPurify.sanitize(decodedDetails, {
            USE_PROFILES: { html: true },
        });
        html = decodeURIComponent(html);
        setDetails(html);
        setEditorKey(editorKey * 2);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors([]);
        setIsLoading(true);

        let content = details;
        if (content.length === 0) {
            content = "<p></p>";
        }

        const inputs = {
            id: post._id,
            title,
            subTitle,
            details: content,
            media
        };
        let response = null;
        response = await updatePost(inputs);
        setIsLoading(false);
        if (response !== true) {
            return setErrors(response);
        }
        clearForm();
        toast.success('Post was updated successfully', {
            icon: '✅'
        });

        navigate(`/community/${comid}/forum/${id}/post/${pid}`);
    }

    const cancelButtonRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toggleDeleteModal = () => {
        setIsModalOpen(!isModalOpen);
    };
    const closeFun = () => {
        setIsModalOpen(false);
    }
    const goToLogin = () => {
        navigate(`/login`);
    }

    const handleDelete = async (e) => {
        e.preventDefault();
        setErrors([]);
        setIsLoading(true);

        const inputs = {
            id: post._id
        };
        let response = null;
        response = await deletePost(inputs);
        setIsLoading(false);
        if (response !== true) {
            return setErrors(response);
        }
        clearForm();
        toast.success('The Post was deleted successfully', {
            icon: '✅'
        });
        navigate(`/community/${comid}/forum/${id}`);
    }

    const handleOnFileChange = async (changeEvent) => {
        const reader = new FileReader();
        reader.onload = async function (onLoadEvent) {
            setIsFileUpLoading(true);
            setMedia(onLoadEvent.target.result);
            //https://www.youtube.com/watch?v=7lhUsK-FxYI
            // const form = e.currentTarget
            // const fileInput = Array.from(form.elements).find(({name}) => name == 'select-image');
            const fileInput = changeEvent.target;
            const formData = new FormData();
            formData.append('file', fileInput.files[0]);
            formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
            const data = await fetch(CLOUDINARY_URL, {
                method: "POST",
                body: formData
            })
                .then(r => r.json())
                .then((data) => {
                    setMedia(data.secure_url);
                    setIsFileUpLoading(false);
                });
        }
        if (reader && reader.readAsDataURL && changeEvent.target.files && changeEvent.target.files.length > 0) {
            reader.readAsDataURL(changeEvent.target.files[0]);
        }
    };

    const [maximumLength, setMaximumLength] = useState(140000);
    const handleEditorUpdate = (text, editor) => {
        const length = editor.getContent({ format: 'text' }).length;
        if (length <= maximumLength) {
            setDetails(text);
        } else {
            toast.error('Maximum content length exceeded. Accepts upto 20,000 words', {
                icon: '🚫',
                duration: 1500
            });
        }
    };

    const goToProfile = (e, user) => {
        e.preventDefault();
        if (loggedInUser) {
            navigate(`/profile/${user._id}`);
        }
    };

    // Function to fetch link preview data
    const fetchLinkPreview = async (targetUrl) => {
        if (lastPreviewedUrl == targetUrl && linkPreviewed != null) {
            return;
        }

        let url = targetUrl;
        // Check if the URL is valid
        if (!url.match(/^(https?:\/\/|www\.)/i)) {
            return;
        }

        // Normalize the URL (add https:// if missing)
        if (url.indexOf('://') === -1) {
            url = 'https://' + url;
        }

        try {
            // console.log("The going to fetch url:::::", url);
            const response = await postLinkPreview(url);
            // console.log("The link preview response ", response);
            if (Array.isArray(response)) {
                setLinkPreviewed(null);
                setLastPreviewedUrl(null);
            } else {
                setLinkPreviewed(response);
                setLastPreviewedUrl(targetUrl);
            }
        } catch (error) {
            console.error('Error fetching link preview:', error);
        }
    };

    const handleCloseClick = () => {
        setLinkPreviewed(null);
        setLastPreviewedUrl(null);
    }

    return (
        <CommunityLayout currentPage={"addpost"} onCommunitiesLoaded={()=>{}} >
            {/* <ForumHeroSection forum={forum} /> */}
            <div className="bg-red-400x min-h-[calc(100vh-60px)] md:min-h-[calc(100vh-125px)] md:min-h-none mt-14 lg:mt-0 flex-1 flex flex-col  ">

                <ForumHeaderSection
                    forum={forum}
                    breadcrumbPages={breadcrumbPages}
                />

                <form action="#" method="POST" onSubmit={handleSubmit} className="bg-green-400x flex flex-col flex-grow bg-primary-white  max-h-[815px] overflow-auto rounded-b-md drop-shadow-sm  md:mb-0 border border-gray-900/10">

                    {/* <h2 className="text-lg font-semibold rounded-md  text-gray-900 px-4 pt-2 pb-0 mb-0 flex justify-between">
                                Edit Post Form
                                {/* {(loggedInUser && post && (loggedInUser._id === post.user._id || loggedInUser.role === "admin")) &&
                                            <button role="button" 
                                                type="button"
                                                disabled={isLoading}
                                                className="rounded-sm bg-red-600 px-3 py-1 text-sm font-semibold text-white  hover:bg-red-500 disabled:cursor-not-allowed"
                                                onClick={toggleDeleteModal}
                                            >
                                                Delete
                                            </button>
                                        } *
                            </h2> */}
                    {/* <p className=" text-sm text-gray-600 px-4 pt-2 pt-0">
                                Fill out and submit this form to update the post.
                            </p> */}


                    <div class="p-2 flex flex-row justify-between items-center ">
                        {loggedInUser &&
                            <div class="flex flex-row items-center gap-x-2">
                                <NavUserAvatar user={loggedInUser} link={"#"} imgSizeClass={`w-[48px] h-[48px] max-w-[48px] max-h-[48px] block`} />

                                <div className=" flex flex-col justify-between flex-grow ">
                                    <div className={`text-xs font-semibold leading-6x text-primary-gray-900`}>
                                        <div onClick={(e) => goToProfile(e, loggedInUser)} className="whitespace-nowrap cursor-pointer">
                                            <div style={{ fontSize: "16px" }}>{loggedInUser.username}</div>
                                            {/* <div className="absolute inset-x-2 top-[4px] bottom-0 font-bold w-fit" >
                                                                    {index+1}
                                                                </div> */}
                                        </div>
                                    </div>
                                    <div className={`mt-1 text-sm flex leading-1 text-gray-900 whitespace-nowrap capitalize`}>
                                        Edit Post
                                    </div>
                                </div>
                            </div>
                        }



                        {/* <button
                                                        disabled={isLoading}
                                                        onClick={togglePostType}
                                                        className="pointer flex flex-row gap-x-[2px] cursor-pointer rounded-sm text-purple-900 px-3 py-1.5 text-sm md:text-md font-bold hover:text-purple-800 "
                                                    >
                                                        <ArticleIcon />
                                                        Convert To {postType === 'post' ? 'Article' : 'Post'}
                                                    </button> */}
                    </div>

                    <div className="bg-white flex-grow  border-t border-purple-900/10  ">
                        {/* title */}
                        {/* {postType === 'article' && */}
                            <div className="col-span-full p-4">
                                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                                    <b>Title</b>
                                    <span className="text-red-900 mx-1">*</span>
                                </label>
                                <div className="mt-2">
                                    <input
                                        type="text"
                                        name="title"
                                        id="title"
                                        autoComplete="title"
                                        value={title}
                                        disabled={isLoading}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="!text-xl block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-progress"
                                    />
                                </div>
                            </div>
                        {/* } */}
                        {/* sub title */}
                        {/* <div className="col-span-full">
                                            <label htmlFor="sub-title" className="block text-sm font-medium leading-6 text-gray-900">
                                                <b>Sub Title</b>
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    type="text"
                                                    name="sub-title"
                                                    id="sub-title"
                                                    value={subTitle}
                                                    disabled={isLoading}
                                                    onChange={(e) => setSubTitle(e.target.value)}
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:cursor-progress"
                                                />
                                            </div>
                                        </div> */}
                        {/* media */}
                        <div className="col-span-full mb-4 hidden">

                            <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                <b>Image</b>
                            </label>

                            <div className="flex items-start justify-start bg-grey-lighter mt-2">
                                <label disabled={isLoading} htmlFor="select-image" className={"flex flex-row items-center px-3 py-0.5 bg-white text-blue rounded-sm shadow-sm tracking-wide  border border-blue  hover:bg-purple-100 hover:text-gray-400 gap-x-2 " + (isLoading ? "cursor-not-allowed" : "cursor-pointer")}>
                                    {!isFileUpLoading &&
                                        <svg className="w-5 h-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M16.88 9.1A4 4 0 0 1 16 17H5a5 5 0 0 1-1-9.9V7a3 3 0 0 1 4.52-2.59A4.98 4.98 0 0 1 17 8c0 .38-.04.74-.12 1.1zM11 11h3l-4-4-4 4h3v3h2v-3z" />
                                        </svg>
                                    }
                                    <span className={`text-xs font-medium leading-6 text-gray-900 ${isFileUpLoading ? 'flex items-center pr-2 bg-purple-100' : ''} `}>
                                        {isFileUpLoading && <Loader type="ball-beat" style={{ transform: 'scale(0.5)' }} />}
                                        {!isFileUpLoading &&
                                            <span>
                                                {(!media || media.length === 0) ? 'Upload' : 'Upload'}  Picture
                                            </span>
                                        }
                                        {isFileUpLoading && "uploading"}
                                    </span>
                                    <input id="select-image" disabled={isLoading} name="select-image" type='file' className="hidden" onChange={handleOnFileChange} />
                                </label>
                            </div>

                            {media.length > 0 &&
                                <div className="flex flex-col mt-2 m" >
                                    <img
                                        className="inline-block max-h-[350px] rounded-sm"
                                        src={media}
                                        alt=""
                                    />
                                </div>
                            }
                        </div>

                        {postType === 'article' &&
                            <div className={`col-span-full px-4 `}>
                                <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900">
                                    <b>Article Details</b>
                                    <span className="text-red-900 mx-1">*</span>
                                    {/* <span className="mt-3 ml-2 text-xs leading-1 text-gray-400">
                                                                            - Provide details of your post
                                                                        </span> */}
                                </label>

                                <div className="flex flex-col border border-purple-900/10 rounded-md pb-4 ">
                                    <div className="article-editor px-4">

                                        <RichTextEditor value={details} onChange={setDetails} />

                                        {/* <Editor
                                            id="add-post-form"
                                            key={'editor1-' + editorKey}
                                            apiKey={TINY_MCE_API_KEY}
                                            onInit={(evt, editor) => editorRef.current = editor}
                                            onEditorChange={handleEditorUpdate}
                                            value={details}
                                            init={{
                                                min_height: 280,
                                                menubar: false,
                                                plugins:
                                                    'advlist link autolink lists link image  anchor ' +
                                                    'searchreplace visualblocks ' +
                                                    'media table past wordcount autoresize emoticons'
                                                ,
                                                toolbar: 'undo redo | link formatselect | fontfamily fontsize | ' +
                                                    'bold italic emoticons backcolor | alignleft aligncenter ' +
                                                    'alignright alignjustify | outdent indent | ' +
                                                    'bullist numlist',
                                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:18px }',
                                                font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
                                                content_style: `
                                                                                .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
                                                                                    //color: rgba(34,47,62,.7);
                                                                                    left: 5px !important;
                                                                                }
                                                                                
                                                                                .mce-content-body p {
                                                                                    margin-top: 0;
                                                                                    margin-bottom: 0;
                                                                                }
                                                                            `,

                                            }}
                                        /> */}
                                    </div>

                                    {media.length > 0 &&
                                        <div className="flex flex-col mt-2 px-4 relative" >
                                            {/* <img
                                                                    className="inline-block max-h-[350px] rounded-sm"
                                                                    src={media}
                                                                    alt=""
                                                                /> */}

                                            <button
                                                type="button"
                                                class="absolute top-1 right-5  z-10 p-1.5 rounded-full bg-gray-900/70 bg-gray-900 text-white  transition-all duration-200 hover:scale-110"
                                                aria-label="Close preview"
                                                onClick={() => setMedia("")}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x">
                                                    <path d="M18 6 6 18"></path>
                                                    <path d="m6 6 12 12"></path>
                                                </svg>
                                            </button>

                                            <div className="w-full h-[270px] bg-no-repeat bg-center bg-cover rounded-md"
                                                style={{ backgroundImage: `url(${media})`, backgroundColor: "#fafafa" }}
                                            >
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        {/* description */}
                        {postType === 'post' &&
                            <div className="col-span-full">

                                <div className="post-editor">

                                    <RichTextEditor value={details} onChange={setDetails} />

                                    {/* <Editor
                                            key={'editor1-' + editorKey}
                                            apiKey={TINY_MCE_API_KEY}
                                            onInit={(evt, editor) => editorRef.current = editor}
                                            onEditorChange={handleEditorUpdate}
                                            value={details}
                                            init={{
                                                height: 250,
                                                menubar: false,
                                                plugins:
                                                    'link advlist autolink lists link image  anchor ' +
                                                    'searchreplace visualblocks ' +
                                                    'media table past wordcount'
                                                ,
                                                toolbar: 'undo redo | link formatselect | fontfamily fontsize | ' +
                                                    'bold italic backcolor | alignleft aligncenter ' +
                                                    'alignright alignjustify | outdent indent | ' +
                                                    'bullist numlist',
                                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                                font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt'
                                            }}
                                        /> */}

                                    {/* <Editor
                                        id="edit-post-form"
                                        key={'editor1-' + editorKey}
                                        apiKey={TINY_MCE_API_KEY}
                                        onInit={(evt, editor) => editorRef.current = editor}
                                        onEditorChange={handleEditorUpdate}
                                        value={details}
                                        init={{
                                            toolbar: 'bold italic emoticons',
                                            menubar: false,
                                            statusbar: false,
                                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:20px }',
                                            placeholder: ` What is on your mind ?`,
                                            auto_focus: 'add-post-form',
                                            plugins: 'autoresize autolink emoticons wordcount',
                                            autoresize_overflow_padding: 5,
                                            autoresize_bottom_margin: '25s',
                                            content_style: `
                                                                                                .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
                                                                                                    //color: rgba(34,47,62,.7);
                                                                                                    left: 5px !important;
                                                                                                }
                                                                                                
                                                                                                .mce-content-body p {
                                                                                                    margin-top: 0;
                                                                                                    margin-bottom: 0;
                                                                                                }
                                                                                            `,
                                            setup: function (editor) {
                                                // console.log("The editor is initialized", editor);

                                                editor.on('NodeChange', function (e) {
                                                    if (e.element.nodeName === 'A') {
                                                        // console.log("Link detected in editor");
                                                        const url = e.element.getAttribute('href');
                                                        // console.log("url", url);
                                                        if (url) {
                                                            fetchLinkPreview(url, editor, e.element);
                                                        }
                                                    }
                                                });
                                            }
                                        }}
                                    /> */}
                                </div>

                                {media.length > 0 &&
                                    <div className="flex flex-col mt-2 px-4 relative" >
                                        <button
                                            type="button"
                                            class="absolute top-1 right-5 z-10 p-1.5 rounded-full bg-gray-900/70 bg-gray-900 text-white  transition-all duration-200 hover:scale-110"
                                            aria-label="Close preview"
                                            onClick={() => setMedia("")}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x">
                                                <path d="M18 6 6 18"></path>
                                                <path d="m6 6 12 12"></path>
                                            </svg>
                                        </button>

                                        <div className="w-full h-[270px] bg-no-repeat bg-center bg-cover rounded-md"
                                            style={{ backgroundImage: `url(${media})`, backgroundColor: "#fafafa" }}
                                        >
                                        </div>
                                    </div>
                                }

                            </div>
                        }

                        {linkPreviewed &&
                            <LinkPreviewUI data={linkPreviewed} handleCloseClick={handleCloseClick} />
                        }

                    </div>

                    <div className="mt-2 py-4 px-4 border-t border-purple-900/10 flex flex-row justify-between ">

                        <div className="flex flex-row justify-start gap-x-2 items-center">
                            {/* <button role="button"
                                type="button"
                                disabled={isLoading}
                                className="w-fit flex justify-center rounded-sm  px-3 py-2 text-md font-semibold text-gray-900 hover:text-gray-700  disabled:cursor-not-allowed"
                                onClick={toggleEmojiPicker}
                            >
                                <FaceSmileIcon className="w-6 h-6" />
                            </button> */}

                            <label disabled={isLoading} htmlFor="select-image"
                                className="w-fit flex justify-center rounded-sm  px-3 py-2 text-md font-semibold text-gray-900 hover:text-gray-700  disabled:cursor-not-allowed"
                            >
                                <PhotoIcon className="w-6 h-6" />
                                <input id="select-image" disabled={isLoading} name="select-image" type='file' className="hidden" onChange={handleOnFileChange} />
                            </label>
                        </div>

                        <div className="flex flex-row justify-start gap-x-2 items-center">
                            <a
                                href={`/community/${comid}/forum/${id}/post/${pid}`}
                                className="w-fit  flex justify-center cursor-pointer items-center flex-row w-1/4 rounded-md bg-gray-400 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-gray-300 disabled:cursor-not-allowed "
                            >
                                Cancel
                            </a>

                            <button role="button"
                                type="submit"
                                disabled={isLoading}
                                className="w-fit  flex justify-center rounded-md bg-purple-900 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-800 disabled:cursor-not-allowed"
                            >
                                {isLoading && <Loader type="ball-beat" style={{ transform: 'scale(0.5)' }} />}
                                Submit
                            </button>
                        </div>

                    </div>

                    {errors.length > 0 &&
                        <div className="app-error text-red-400 font-bold text-xs py-5 px-10">
                            {errors[0]}
                        </div>
                    }
                </form>
                <Transition.Root show={isModalOpen} as={Fragment}>
                    <Dialog as="div" className="relative " style={{ zIndex: 99 }} initialFocus={cancelButtonRef} open={isModalOpen} onClose={closeFun}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div onClick={() => closeFun(false)} className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div className="flex min-h-full md:items-center md:justify-center p-4 md:text-center sm:items-center sm:p-0">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <Dialog.Panel className="relative transform overflow-hidden  px-0 pb-0 pt-0 text-left shadow-xl transition-all w-[93vw]  md:w-full md:max-w-3xl md:mx-56">

                                        <div className="bg-white mx-4 rounded-md">

                                            <div className="bg-primary-white border-b border-gray-200 flex flex-row justify-between p-3 rounded-t-md items-center ">
                                                <div className="text-gray-900 text-md font-bold">
                                                    Delete This Post
                                                </div>
                                            </div>

                                            <div className="overflow-x-auto rounded-b-sm p-5 text-lg">
                                                Deleting the post deletes all its comments and likes. Do you want to continue with this action ?
                                            </div>

                                            <div className="bg-white border-b border-gray-200 flex flex-row justify-between p-3 rounded-b-md items-center ">
                                                <button role="button"
                                                    onClick={(e) => setIsModalOpen(false)}
                                                    className="flex justify-center cursor-pointer items-center flex-row w-1/4 rounded-sm bg-gray-400 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-gray-300 disabled:cursor-not-allowed "
                                                >
                                                    Cancel
                                                </button>

                                                <button role="button"
                                                    type="button"
                                                    disabled={isLoading}
                                                    onClick={handleDelete}
                                                    className="w-[200px] flex justify-center rounded-sm bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed"
                                                >
                                                    {isLoading && <Loader type="ball-beat" style={{ transform: 'scale(0.5)' }} />}
                                                    Delete
                                                </button>
                                            </div>
                                        </div>

                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            </div>
        </CommunityLayout>
    );
};

export default EditPostForm;
